import React, { Component } from "react";
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TablePagination,
    Snackbar, Alert
} from '@mui/material';
import { GridTable } from "../Sidebar/SidebarStyles";
import { fetchAllLobs } from '../ApiSevices/FileUploadApis';
import { fetchDataCsrDashboard, generateExportExcelforCsrRole } from '../ApiSevices/DashboardApis';

interface State {
    clientName: string;
    policyNumber: string;
    lineOfCoverage: string;
    jobId: string;
    fromDate: any;
    toDate: any;
    lobCodes: { Lob_Name: string; Lob_Code: string }[];
    policyType: string;
    data: any;
    loading: boolean;
    page: number;
    rowsPerPage: number;
    totalCount: number;
    snackbarOpen: boolean;
    snackbarMessage: string;
    snackbarSeverity: 'success' | 'error' | 'info' | 'warning';
}

interface Props {
    displaysidebar: boolean;
}

class CsrDashboard extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        const today = new Date();
        const toDate = today.toISOString().slice(0, 10);
        const fromDateObj = new Date(today);
        fromDateObj.setMonth(fromDateObj.getMonth() - 1);

        if (fromDateObj.getDate() !== today.getDate()) {
            fromDateObj.setDate(0);
        }

        const fromDate = fromDateObj.toISOString().slice(0, 10);

        this.state = {
            clientName: "",
            policyNumber: "",
            lineOfCoverage: "",
            jobId: "",
            fromDate: fromDate,
            toDate: toDate,
            data: [],
            lobCodes: [],
            policyType: "",
            loading: false,
            page: 0,
            rowsPerPage: 100,
            totalCount: 0,
            snackbarOpen: false,
            snackbarMessage: '',
            snackbarSeverity: 'success',
        };
    }

    componentDidMount() {
        this.setState({ loading: true }, async () => {
            try {
                await Promise.all([this.fetchLobs(false)]);
            } catch (error) {
                console.error('Error during initial data fetch:', error);
            } finally {
                this.setState({ loading: false });
            }
        });
    }


    showSnackbar = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
        this.setState({ snackbarOpen: true, snackbarMessage: message, snackbarSeverity: severity });
    };

    handleCloseSnackbar = () => {
        this.setState({ snackbarOpen: false });
    };


    fetchLobs = async (manageLoading: boolean = true) => {
        if (manageLoading) this.setState({ loading: true });
        try {
            const fetchResponse = await fetchAllLobs();
            if (fetchResponse && fetchResponse?.length > 0) {
                this.setState({ lobCodes: Array.from(new Set(fetchResponse)) });
            }
        } catch (error) {
            console.error('Error fetching lobData:', error);
        } finally {
            if (manageLoading) this.setState({ loading: false });
        }
    };

    fetchDashboardData = async (manageLoading: boolean = true) => {
        const {
            fromDate,
            toDate,
            clientName,
            policyNumber,
            lineOfCoverage,
            policyType,
            jobId,
            page,
            rowsPerPage
        } = this.state;
        if (manageLoading) this.setState({ loading: true });

        try {
            const fetchResponse = await fetchDataCsrDashboard(
                fromDate,
                toDate,
                clientName,
                policyNumber,
                lineOfCoverage,
                policyType,
                jobId,
                page,
                rowsPerPage
            );
            if (fetchResponse && fetchResponse?.data?.data?.length > 0) {
                this.setState({
                    data: fetchResponse?.data?.data,
                    totalCount: fetchResponse?.data?.totalCount
                });
            } else {
                this.setState({ data: [], totalCount: 0 });
                this.showSnackbar("No Records are found", 'error');
            }
        } catch (error) {
            console.error('Error fetching dashboardData:', error);
        } finally {
            if (manageLoading) this.setState({ loading: false });
        }
    };

    handleExportExcel = async () => {
        this.setState({ loading: true });
        const {
            fromDate,
            toDate,
            clientName,
            policyNumber,
            lineOfCoverage,
            policyType,
            jobId,
        } = this.state;
        try {
            const fileBlob = await generateExportExcelforCsrRole(fromDate, toDate, clientName, policyNumber, lineOfCoverage, policyType, jobId);

            const url = window.URL.createObjectURL(fileBlob);
            const link = document.createElement('a');
            link.href = url;

            const now = new Date();
            const day = String(now.getDate()).padStart(2, '0');
            const month = String(now.getMonth() + 1).padStart(2, '0');
            const year = now.getFullYear();

            const formattedDate = `${day}${month}${year}`;
            const filename = `MIS_${formattedDate}.xlsx`;
            link.setAttribute('download', filename);

            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error exporting production report:', error);
        } finally {
            this.setState({ loading: false });
        }
    };

    handleSearch = () => {
        this.setState({ page: 0 }, this.fetchDashboardData);
    };

    handlePageChange = (event: any, newPage: number) => {
        this.setState({ page: newPage }, this.fetchDashboardData);
    };

    handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({
            rowsPerPage: parseInt(event.target.value, 10),
            page: 0
        }, this.fetchDashboardData);
    };

    handleChange = (field: string, value: any) => {
        this.setState({ [field]: value?.trim() } as any);
    };

    render() {
        const {
            clientName,
            policyNumber,
            lineOfCoverage,
            jobId,
            fromDate,
            toDate,
            data,
            policyType,
            loading,
            lobCodes,
            page,
            rowsPerPage,
            totalCount,
            snackbarOpen, snackbarMessage, snackbarSeverity
        } = this.state;

        const { displaysidebar } = this.props;
        const todayMax = new Date().toISOString().split('T')[0];

        return (
            <>
                {loading && (
                    <Box sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        backgroundColor: 'rgba(255, 255, 255, 0.5)',
                        zIndex: 9999
                    }}>
                        <div className="loader"></div>
                    </Box>
                )}
                <GridTable displaysidebar={displaysidebar}>
                    <div className="dashboard-container">
                        <div className="form-fields">
                            <input
                                type="text"
                                placeholder="Enter Client Name"
                                value={clientName}
                                onChange={(e) => this.handleChange("clientName", e.target.value?.toLowerCase())}
                            />
                            <input
                                type="text"
                                placeholder="Enter Policy #"
                                value={policyNumber}
                                onChange={(e) => this.handleChange("policyNumber", e.target.value)}
                            />
                            <select
                                value={lineOfCoverage}
                                onChange={(e) => this.handleChange("lineOfCoverage", e.target.value)}
                            >
                                <option value="">Line of Coverage</option>
                                {lobCodes?.map((item, index) => (
                                    <option key={index} value={item.Lob_Code}>
                                        {item.Lob_Name}
                                    </option>
                                ))}
                            </select>
                            <input
                                type="text"
                                placeholder="Enter Job ID"
                                value={jobId}
                                onChange={(e) => this.handleChange("jobId", e.target.value)}
                            />
                            <select
                                value={policyType}
                                onChange={(e) => this.handleChange("policyType", e.target.value)}
                            >
                                <option value="">Policy Type</option>
                                <option value="AR">Automatic Renewal</option>
                                <option value="MR">Marketed Renewal</option>
                            </select>
                            <input
                                type="date"
                                value={fromDate}
                                max={toDate || todayMax}
                                onChange={(e) => this.handleChange("fromDate", e.target.value)}
                            />
                            <input
                                type="date"
                                value={toDate}
                                min={fromDate}
                                max={todayMax}
                                onChange={(e) => this.handleChange("toDate", e.target.value)}
                            />
                            <button onClick={this.handleSearch}>Search</button>
                            <button onClick={this.handleExportExcel}>Download</button>
                        </div>

                        <div className="table-wrapper">
                            <Paper sx={{ width: '100%', mb: 2, overflow: 'hidden', border: '0.5px solid #7a6e6e54' }}>
                                <Box sx={{ maxHeight: 340, overflowX: 'auto' }}>
                                    <TableContainer sx={{ minWidth: 'max-content' }}>
                                        <Table stickyHeader sx={{ height: '45px' }} size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{ fontSize: '11px', width: 50, fontWeight: 'bold' }}>S.No</TableCell>
                                                    <TableCell sx={{
                                                        fontSize: '12px',
                                                        fontWeight: 'bold',
                                                        textAlign: 'center',
                                                        width: 200,
                                                        maxWidth: 200
                                                    }}>Client Name</TableCell>
                                                    <TableCell sx={{
                                                        fontSize: '12px',
                                                        fontWeight: 'bold',
                                                        textAlign: 'center',
                                                        width: 160,
                                                        minWidth: 160
                                                    }}>Policy #</TableCell>
                                                    <TableCell sx={{
                                                        fontSize: '12px',
                                                        fontWeight: 'bold',
                                                        textAlign: 'center',
                                                        width: 150,
                                                        minWidth: 150
                                                    }}>Line of Coverage</TableCell>
                                                    <TableCell sx={{
                                                        fontSize: '12px',
                                                        fontWeight: 'bold',
                                                        textAlign: 'center',
                                                        width: 180,
                                                        minWidth: 180
                                                    }}>Job ID</TableCell>
                                                    <TableCell sx={{
                                                        fontSize: '12px',
                                                        fontWeight: 'bold',
                                                        textAlign: 'center',
                                                        width: 180,
                                                        minWidth: 180
                                                    }}>Policy Type</TableCell>
                                                    <TableCell sx={{
                                                        fontSize: '12px',
                                                        fontWeight: 'bold',
                                                        textAlign: 'center',
                                                        width: 160,
                                                        minWidth: 160
                                                    }}>Status</TableCell>
                                                    <TableCell sx={{
                                                        fontSize: '12px',
                                                        fontWeight: 'bold',
                                                        textAlign: 'center',
                                                        width: 200,
                                                        minWidth: 200
                                                    }}>Upload Date & Time (UTC)</TableCell>
                                                    <TableCell sx={{
                                                        fontSize: '12px',
                                                        fontWeight: 'bold',
                                                        textAlign: 'center',
                                                        width: 200,
                                                        minWidth: 200
                                                    }}>Uploaded By</TableCell>
                                                    <TableCell sx={{
                                                        fontSize: '12px',
                                                        fontWeight: 'bold',
                                                        textAlign: 'center',
                                                        width: 180,
                                                        minWidth: 180
                                                    }}>Checklist Type</TableCell>
                                                </TableRow>
                                            </TableHead>

                                        </Table>

                                        <Box sx={{ maxHeight: 260, overflowY: 'auto', overflowX: 'auto', scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none', }, }}>
                                            <Table size="small">
                                                <TableBody>
                                                    {data?.length === 0 ? (
                                                        <TableRow>
                                                            <TableCell align="center"
                                                                sx={{
                                                                    fontSize: '14px',
                                                                    fontWeight: 500,
                                                                    color: 'gray',
                                                                    padding: '20px',
                                                                    borderBottom: 'none',
                                                                }}>
                                                                No records to display
                                                            </TableCell>
                                                        </TableRow>
                                                    ) :
                                                        (data?.map((row: any, idx: number) => {
                                                            const getLob = this.state?.lobCodes?.find(d => d?.Lob_Code == row['Line Of Coverage']);
                                                            return (
                                                                <TableRow key={idx}>
                                                                    <TableCell sx={{ fontSize: '11px', width: 50 }}>
                                                                        {page * rowsPerPage + idx + 1}
                                                                    </TableCell>

                                                                    <TableCell
                                                                        sx={{
                                                                            fontSize: '11px',
                                                                            width: 200,
                                                                            maxWidth: 200,
                                                                            whiteSpace: 'normal',
                                                                            wordBreak: 'break-word',
                                                                            overflow: 'hidden',
                                                                            textAlign: "center",
                                                                        }}
                                                                    >
                                                                        {row?.ClientName}
                                                                    </TableCell>

                                                                    <TableCell sx={{ fontSize: '11px', width: 160, textAlign: "center" }}>
                                                                        {row["Policy#"]}
                                                                    </TableCell>

                                                                    <TableCell sx={{ fontSize: '11px', width: 150, textAlign: "center" }}>
                                                                        {row['Line Of Coverage']}
                                                                    </TableCell>

                                                                    <TableCell sx={{
                                                                        fontSize: '11px', width: 180, textAlign: "center"
                                                                    }}>
                                                                        {row?.JobId}
                                                                    </TableCell>

                                                                    <TableCell sx={{
                                                                        fontSize: '11px', width: 180, textAlign: "center"
                                                                    }}>
                                                                        {row?.PolicyType}
                                                                    </TableCell>

                                                                    <TableCell sx={{ fontSize: '11px', width: 160, textAlign: "center" }}>
                                                                        {row?.Status}
                                                                    </TableCell>

                                                                    <TableCell sx={{ fontSize: '11px', width: 200, textAlign: "center" }}>
                                                                        {row?.CreatedOn}
                                                                    </TableCell>

                                                                    <TableCell sx={{ fontSize: '11px', width: 200, textAlign: "center" }}>
                                                                        {row?.UploadedBy}
                                                                    </TableCell>

                                                                    <TableCell sx={{
                                                                        fontSize: '11px',
                                                                        width: 180,
                                                                        maxWidth: 180,
                                                                        textAlign: "center",
                                                                    }}>
                                                                        {row?.ChecklistType}
                                                                    </TableCell>
                                                                </TableRow>
                                                            )
                                                        }))}
                                                </TableBody>
                                            </Table>
                                        </Box>
                                    </TableContainer>
                                </Box>

                                <TablePagination
                                    component="div"
                                    count={totalCount}
                                    page={page}
                                    onPageChange={this.handlePageChange}
                                    rowsPerPage={rowsPerPage}
                                    onRowsPerPageChange={this.handleRowsPerPageChange}
                                    rowsPerPageOptions={[10, 25, 50, 100, 200]}
                                />
                            </Paper>
                        </div>

                    </div>
                </GridTable>
                <Snackbar
                    open={snackbarOpen}
                    autoHideDuration={3000}
                    onClose={this.handleCloseSnackbar}
                    anchorOrigin={{ vertical: "top", horizontal: "right" }}
                >
                    <Alert severity={snackbarSeverity} onClose={this.handleCloseSnackbar}>
                        {snackbarMessage}
                    </Alert>
                </Snackbar>
            </>
        );
    }
}

export default CsrDashboard;
